const months: Record<string, string> = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
};

const isoDateRe =
  /(^([\w ]+,?[\w ]+)?[\w ]+,?[\w ]+\d+:\d+(:\d+)?[\w ]?|^\d{1,4}[/-]\d{1,4}[/-]\d{1,4}|^\w+, \w+ \d+, \d{4})\b/;
const yearGroupRe = /^(\d\d)?(\d\d)\s*[/\\-]\s*(\d\d)?(\d\d)\b/i;
const monthRe =
  /\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/i;
const yearRe = /\b(?:\d\d)\d\d\b/;
const year4Re = /\b\d\d\d\d\b/;
const dayRe = /\b(\d\d?)(?:st|nd|rd|th)?\b/i;
const monthAndYearRe = new RegExp(
  `(?:(${monthRe.source})\\W+(${yearRe.source}))|(?:(${year4Re.source})\\W+(${monthRe.source}))`,
  'i',
);

const monthAndYearReGroups = {
  month: [1, 4],
  year: [2, 3],
  length: 4,
};

const dayMonthYearRe = new RegExp(
  `(?:${dayRe.source}\\W+(${monthRe.source})(?:\\W+(${yearRe.source}))?)|(?:(${monthRe.source})\\W+${dayRe.source}(?:\\W+(${yearRe.source}))?)`,
  'i',
);
const dayMonthYearReGroups = {
  day: [1, 5],
  month: [2, 4],
  year: [3, 6],
  length: 6,
};

const dateRe = new RegExp(
  `^(?:(${monthRe.source})|(?:${monthAndYearRe.source})|(?:${dayMonthYearRe.source}))$`,
  'i',
);
const dateReGroups = calcDateReGroups();

interface DateRegexGroup {
  day: number[];
  month: number[];
  year: number[];
}

function calcDateReGroups() {
  const out: DateRegexGroup = {
    day: [],
    month: [1],
    year: [],
  };

  let offset = 1;
  applyGroup(monthAndYearReGroups);
  applyGroup(dayMonthYearReGroups);

  function applyGroup(group: Partial<DateRegexGroup> & { length: number }) {
    for (const _key of Object.keys(out)) {
      const key = _key as keyof typeof out;
      const existing = out[key];
      out[key] = [
        ...existing,
        ...(group[key as keyof DateRegexGroup] || []).map(
          (idx) => idx + offset,
        ),
      ];
    }

    offset += group.length;
  }

  return {
    ...out,
    length: offset,
  };
}

function getDateMatchVal(match: RegExpMatchArray, key: keyof DateRegexGroup) {
  const possibleVals = dateReGroups[key];
  for (const idx of possibleVals) {
    if (match[idx]) {
      return match[idx];
    }
  }
}

function monthNameToNum(name: string | null | undefined) {
  if (name) {
    const normalized = name.slice(0, 3).toLowerCase();
    return months[normalized];
  }
}

function padDay(day: string | null | undefined) {
  if (day) {
    return day.length === 1 ? '0' + day : day;
  }

  return '01';
}

function normalizeYear(year: string | null | undefined) {
  if (year) {
    return year.length === 2 ? '20' + year : year;
  }

  return '2000';
}

export type NaturalPropSorter<T> = (a: T, b: T) => -1 | 1 | 0;

export function naturalPropSorterFactory<T extends object>(
  property: keyof T,
): NaturalPropSorter<T> {
  return function (a, b) {
    return naturalSort(a[property], b[property]);
  };
}

function parseDate(val: string) {
  const isoDateMatch = val.match(isoDateRe);
  if (isoDateMatch) {
    return Date.parse(isoDateMatch[0]);
  }

  // Could be a year grouping e.g. 2009 - 2010
  const yearGroupMatch = val.match(yearGroupRe);
  if (yearGroupMatch) {
    const startCentury = yearGroupMatch[1] || '20';
    const startYear = yearGroupMatch[2];
    const endCentury = yearGroupMatch[3] || startCentury;
    const endYear = yearGroupMatch[4];

    // If it's just a 1 year range then assume a september end (academic years)
    const start = parseInt(`${startCentury}${startYear}`, 10);
    const end = parseInt(`${endCentury}${endYear}`, 10);

    if (end - start === 1) {
      return Date.parse(`${end}-08-31`);
    }

    return Date.parse(`${end}-12-31`);
  }

  // Could be a date
  const dateMatch = val.match(dateRe);

  if (!dateMatch) {
    return;
  }

  const day = padDay(getDateMatchVal(dateMatch, 'day'));
  const month = monthNameToNum(getDateMatchVal(dateMatch, 'month'));
  const year = normalizeYear(getDateMatchVal(dateMatch, 'year'));

  try {
    if (day && month && year) {
      return Date.parse(`${year}-${month}-${day}`);
    }
  } catch {
    /* empty */
  }
}

/* eslint-disable */
/*
 * Natural Sort algorithm for Javascript - Version 0.8 - Released under MIT license
 * Author: Jim Palmer (based on chunking idea from Dave Koelle)
 *
 * This has been modified from the original
 */
export function naturalSort(_a: unknown, _b: unknown) {
  const a = String(_a);
  const b = String(_b);
  var re =
      /(^([+\-]?(?:\d*)(?:\.\d*)?(?:[eE][+\-]?\d+)?)?$|^0x[\da-fA-F]+$|\d+)/g,
    sre = /^\s+|\s+$/g, // trim pre-post whitespace
    snre = /\s+/g, // normalize all whitespace to single ' ' character
    ore = /^0/,
    i = function (s: string) {
      return (
        (naturalSort.insensitive && ('' + s).toLowerCase()) ||
        '' + s
      ).replace(sre, '');
    },
    // convert all to strings strip whitespace
    x = i(a) || '',
    y = i(b) || '',
    // chunk/tokenize
    xN = x
      .replace(re, '\0$1\0')
      .replace(/\0$/, '')
      .replace(/^\0/, '')
      .split('\0'),
    yN = y
      .replace(re, '\0$1\0')
      .replace(/\0$/, '')
      .replace(/^\0/, '')
      .split('\0'),
    // numeric, hex or date detection
    xD = xN.length !== 1 && parseDate(x),
    yD = (xD && parseDate(y)) || null,
    normChunk = function (s: string, l: number) {
      // normalize spaces; find floats not starting with '0', string or 0 if not defined (Clint Priest)
      return (
        ((!s.match(ore) || l == 1) && parseFloat(s)) ||
        s.replace(snre, ' ').replace(sre, '') ||
        0
      );
    },
    oFxNcL,
    oFyNcL;
  // first try and sort Hex codes or Dates
  if (xD && yD) {
    if (xD > yD) {
      return -1;
    } else if (xD < yD) {
      return 1;
    }
  }
  // natural sorting through split numeric strings and default strings
  for (
    var cLoc = 0, xNl = xN.length, yNl = yN.length, numS = Math.max(xNl, yNl);
    cLoc < numS;
    cLoc++
  ) {
    oFxNcL = normChunk(xN[cLoc] || '', xNl);
    oFyNcL = normChunk(yN[cLoc] || '', yNl);
    // handle numeric vs string comparison - number < string - (Kyle Adams)
    if (isNaN(oFxNcL as number) !== isNaN(oFyNcL as number)) {
      return isNaN(oFxNcL as number) ? 1 : -1;
    } else if (typeof oFxNcL !== typeof oFyNcL) {
      // rely on string comparison if different types - i.e. '02' < 2 != '02' < '2'
      oFxNcL += '';
      oFyNcL += '';
    }
    if (oFxNcL < oFyNcL) {
      return -1;
    }
    if (oFxNcL > oFyNcL) {
      return 1;
    }
  }
  return 0;
}
/* eslint-enable */

naturalSort.insensitive = true;
